import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiGet, apiPost, parseApiError } from '@/lib/api';

/**
 * Today's beat state for the signed-in Sales Executive — one query, one
 * mutation, shared by every place that needs to show or change it (the top
 * bar in AppShell, the My Beat Today page). Defined once so there is exactly
 * one Beat state machine, never a second one that could drift from the
 * approval workflow the backend actually enforces.
 */

export interface BeatRef {
  id: string;
  name: string;
  code: string;
  areaName: string | null;
}

export interface PendingBeatRequest {
  id: string;
  fromRouteName: string | null;
  requestedRouteId: string;
  requestedRouteName: string;
  requestedAt: string;
  /** The executive's own stated reason — distinct from the admin's decision remarks. */
  reason: string | null;
}

export interface MyBeatTodayResponse {
  businessDate: string;
  weekday: number;
  defaultBeat: BeatRef | null;
  activeBeat: BeatRef | null;
  allowedBeatPool: BeatRef[];
  pendingRequest: PendingBeatRequest | null;
  retailers: Array<{
    id: string;
    routeId: string;
    shopName: string;
    ownerName: string | null;
    phone: string | null;
    imageUrl: string | null;
    addressLine1: string | null;
    city: string | null;
    stateCode: string | null;
    pincode: string | null;
    latitude: number | string | null;
    longitude: number | string | null;
    accuracy: number | string | null;
  }>;
  attendanceId: string | null;
  punchedIn: boolean;
}

export const MY_BEAT_TODAY_QUERY_KEY = ['my-beat-today'];

/** Matches the backend's `reason VARCHAR(500)` / `max:500` validation exactly. */
export const BEAT_CHANGE_REASON_MAX_LENGTH = 500;

export function useMyBeatToday(enabled = true) {
  return useQuery({
    queryKey: MY_BEAT_TODAY_QUERY_KEY,
    queryFn: () => apiGet<MyBeatTodayResponse>('/sales-executives/me/beat-today'),
    enabled,
  });
}

export function useRequestBeatChange(onSuccess?: () => void) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ requestedRouteId, reason }: { requestedRouteId: string; reason: string }) =>
      apiPost<{ message?: string }>('/sales-executives/me/beat-change-requests', { requestedRouteId, reason }),
    onSuccess: () => {
      toast.success('Beat change requested. Waiting for admin approval.');
      void queryClient.invalidateQueries({ queryKey: MY_BEAT_TODAY_QUERY_KEY });
      onSuccess?.();
    },
    onError: (error) => toast.error(parseApiError(error).message),
  });
}
